import isEmpty from 'lodash/isEmpty'
import get from 'lodash/get'
import { useUser } from '../useUser'
import { useList } from '../useList'
import { queries } from './graphql'
import type { AppSetting } from '@ztechapps/common.contract'

/***
 * Retrieve multiple settings
 * @param appSettingKeys
 * @param filter
 * @param tenantId
 * @param useList
 */
export const useAppSettings = ({
  tenantId,
  appSettingKeys,
  filter,
}: {
  tenantId?: string
  appSettingKeys?: string[]
  filter?: Record<string, any>
}) => {
  const useResult = useUser()
  tenantId = tenantId ?? useResult?.user?.tenantId

  //when the appSettingKeys is available, use it on filtering values
  if (!isEmpty(appSettingKeys)) {
    filter = {
      key: { $in: appSettingKeys },
      'scope.key': tenantId,
    }
  }

  const {
    list: settings,
    loading,
    error,
  } = useList<AppSetting>({
    ...queries.allConfig,
    limit: 0,
    filter,
    options: {
      queryOptions: {
        fetchPolicy: 'cache-first',
        nextFetchPolicy: 'cache-first',
        skip: !tenantId && isEmpty(appSettingKeys),
      },
    },
  })

  return { settings, loading, error }
}

export type UseAppSettingProps = { tenantId?: string; appSettingKey?: string; filter?: Record<string, any> }
export type UseAppSettingResult = { setting: AppSetting; loading: boolean; error: any }
/***
 * Retrieve and update single setting
 */
export const useAppSetting = ({ tenantId, appSettingKey, filter }: UseAppSettingProps): UseAppSettingResult => {
  const useResult = useUser()
  tenantId = tenantId ?? useResult?.user?.tenantId

  const { settings, loading, error } = useAppSettings({
    tenantId,
    appSettingKeys: appSettingKey ? [appSettingKey] : undefined,
    filter,
  })

  return { setting: get(settings, '[0]'), loading, error }
}

export type UseAppSettingValueResult = { loading: boolean; error: any; value: string }
export type UseAppSettingValueProps = UseAppSettingProps
export const useAppSettingValue = (props: UseAppSettingValueProps): UseAppSettingValueResult => {
  const { setting, loading, error } = useAppSetting(props)

  return {
    value: setting?.value,
    loading,
    error,
  }
}
